import { localizedStrings } from '@core/strings'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { ControllerRenderProps } from 'react-hook-form'
import { Box, BoxProps, Flex, Text } from '@fundamentals'
import Dropzone from 'react-dropzone'
import { Button } from '@common/material'
import { useModal } from '@common/GlobalModal'
import produce from 'immer'
import { AttachmentTypes } from '@core/components/templateAnswersForm'
import {
  SelectSiteBoxDocumentModal,
  siteBoxDocumentToProof,
} from '@modules/siteBox/modals/SelectSiteBoxDocumentModal'
import { useNavigationContext } from '@common/hooks'
import { useToast } from '@core/toast'
import useFeatureFlags from '@core/providers/useFeatureFlags'
import { SelectSiteBoxDocumentModalLegacy } from '@modules/siteBox/modals/SelectSiteBoxDocumentModalLegacy'

import { ImageWarning } from '@core/entities/ImageWarning'
import fileTypeChecker from 'file-type-checker'
import { SiteBoxDocument } from '@core/entities/SiteBoxDocument/SiteBoxDocument'
import { FileFormat } from '@core/entities/SiteBoxDocument'
import { Palette } from '@core/theme/color-palette-siteassist'
import { Proof } from '@core/entities/template/TemplateQuestion'
import { AttachmentFrame } from './AttachmentFrame'
import { AttachmentThumbnail } from './AttachmentThumbnail'

/**
 * Fields that are present on a SiteBox document attachment.
 * `siteBoxDocumentVersionId` is required — it acts as the discriminant.
 */
type SiteBoxDocumentFields = Pick<Proof, 'siteBoxDocumentVersionId' | 'name'>

export type AttachmentInputValue = Blob & {
  uri?: string
  key?: string
  id?: number
  siteBoxDocument?: SiteBoxDocument
  downloadUrl?: string
  getUrl?: string
  nonDeletable?: boolean
} & (SiteBoxDocumentFields | { [K in keyof SiteBoxDocumentFields]?: never })

type AttachmentInputProps = BoxProps & {
  enableLinkSiteBoxDocuments?: boolean
  inputProps?: Partial<ControllerRenderProps>
  error?: string
  maxFiles?: number
  maxFileSize?: number
  readOnly?: boolean
  attachmentTypes?: AttachmentTypes[]
  required?: boolean
  heading?: string
  borderLess?: boolean
  size?: 'small' | 'large'
  projectId?: number
  multiple?: boolean
  warnings?: ImageWarning[]
  preventDialogClose?: boolean
  thumbnailFitToContent?: boolean
  capturedByName?: string
  replaceMode?: boolean
  showFileNames?: boolean
  attachmentLayout?: 'grid' | 'list'
}

/**
 * Immutably replace the proof at `index` with an annotated version of `blob`.
 * SiteBox-linked proofs are cloned as a normal local File.
 *
 * Note: assigns individual fields rather than spreading `original` onto the
 * blob — Blob has read-only getters for `type`/`size`, so a naive spread
 * throws "Cannot set property type".
 */
const replaceProofAt = (
  list: Array<AttachmentInputValue>,
  index: number,
  blob: Blob,
  url: string,
  original: AttachmentInputValue,
): Array<AttachmentInputValue> => {
  const originalName = (original as File).name
  const annotatedFile = originalName
    ? new File([blob], originalName, {
        type: blob.type || 'application/pdf',
      })
    : blob
  const annotated = annotatedFile as AttachmentInputValue
  annotated.uri = url

  const siteBoxDocumentVersionId =
    original.siteBoxDocumentVersionId ??
    original.siteBoxDocument?.currentVersion?.id

  if (siteBoxDocumentVersionId !== undefined) {
    annotated.siteBoxDocumentVersionId = siteBoxDocumentVersionId
  } else {
    annotated.id = original.id
    annotated.key = original.key
    annotated.downloadUrl = original.downloadUrl
    annotated.getUrl = original.getUrl
  }

  const next = [...list]
  next[index] = annotated
  return next
}

/**
 * Validates image files by checking their actual content (magic bytes)
 * rather than just the file extension. This prevents users from
 * uploading HEIC files renamed to .jpg/.jpeg
 */
const validateImageFile = (
  file: File,
): Promise<{ valid: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer

      // Check for HEIC/HEIF files (not supported)
      if (fileTypeChecker.isHEIC(buffer)) {
        resolve({
          valid: false,
          error: localizedStrings.heicFileNotSupported,
        })
        return
      }

      // Validate that the file is actually a valid JPEG or PNG
      const isValidImage =
        fileTypeChecker.isJPEG(buffer) || fileTypeChecker.isPNG(buffer)

      if (!isValidImage) {
        resolve({
          valid: false,
          error: localizedStrings.invalidImageFile,
        })
        return
      }

      resolve({ valid: true })
    }

    reader.onerror = () => {
      resolve({ valid: false, error: localizedStrings.fileReadError })
    }

    // Read first 4100 bytes (enough for magic byte detection)
    reader.readAsArrayBuffer(file.slice(0, 4100))
  })
}

/**
 * Validates PDF files by checking their actual content (magic bytes)
 * rather than just the file extension. This prevents users from
 * uploading corrupted or invalid PDF files.
 */
const validatePdfFile = (
  file: File,
): Promise<{ valid: boolean; error?: string }> => {
  return new Promise((resolve) => {
    const reader = new FileReader()

    reader.onload = () => {
      const buffer = reader.result as ArrayBuffer

      // Validate that the file is actually a valid PDF
      const isValidPdf = fileTypeChecker.isPDF(buffer)

      if (!isValidPdf) {
        resolve({
          valid: false,
          error: localizedStrings.invalidPdfFile,
        })
        return
      }

      resolve({ valid: true })
    }

    reader.onerror = () => {
      resolve({ valid: false, error: localizedStrings.fileReadError })
    }

    // Read first 4100 bytes (enough for magic byte detection)
    reader.readAsArrayBuffer(file.slice(0, 4100))
  })
}

type Attachments = Array<File & { uri?: string }>

const AttachmentInput: React.FC<AttachmentInputProps> = ({
  enableLinkSiteBoxDocuments = false,
  multiple = true,
  inputProps: { value = [], onChange, ...inputProps } = {},
  readOnly = false,
  attachmentTypes,
  required,
  dataTest,
  heading,
  borderLess,
  size = 'small',
  projectId,
  maxFiles,
  warnings,
  preventDialogClose = true,
  thumbnailFitToContent = false,
  capturedByName,
  replaceMode = false,
  showFileNames = false,
  borderColor = 'silver',
  attachmentLayout = 'grid',
  ...props
}) => {
  const { organisationId } = useNavigationContext()
  const { showModal, hideModal } = useModal()
  const toast = useToast()
  const { flags } = useFeatureFlags(['web_site_box_rams_appraisals'])

  const isListLayout = attachmentLayout === 'list'

  const valueRef = useRef<Array<AttachmentInputValue>>(value)
  valueRef.current = value

  const updateValue = useCallback(
    (newValue: Array<AttachmentInputValue>) => {
      valueRef.current = newValue
      onChange(newValue)
    },
    [onChange],
  )

  const pasteContainerRef = useRef<HTMLDivElement>(null)

  const acceptTypes = useMemo(() => {
    const types = {}
    attachmentTypes?.forEach((attachmentInput) => {
      if (attachmentInput === AttachmentTypes.IMAGE) {
        types['image/png'] = ['.png']
        types['image/jpeg'] = ['.jpeg']
        types['image/jpg'] = ['.jpg']
      }
      if (attachmentInput === AttachmentTypes.PDF) {
        types['application/pdf'] = ['.pdf']
      }
      if (attachmentInput === AttachmentTypes.CSV) {
        types['text/csv'] = ['.csv']
      }
      if (attachmentInput === AttachmentTypes.WORD) {
        types['application/msword'] = ['.doc']
        types[
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ] = ['.docx']
      }
      if (attachmentInput === AttachmentTypes.EXCEL) {
        types['application/vnd.ms-excel'] = ['.xls']
        types[
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        ] = ['.xlsx']
      }
      if (attachmentInput === AttachmentTypes.CAD) {
        types['image/vnd.dwg'] = ['.dwg']
        types['image/vnd.dxf'] = ['.dxf']
      }
      if (attachmentInput === AttachmentTypes.VIDEO) {
        types['video/mp4'] = ['.mp4']
      }
    })
    return types
  }, [attachmentTypes])

  // Which SiteBox document formats this question can link as a proof in question answers
  const allowedSiteBoxProofFormats = useMemo<FileFormat[] | undefined>(() => {
    if (!attachmentTypes?.length) return undefined

    const formats: FileFormat[] = []

    if (attachmentTypes.includes(AttachmentTypes.PDF)) formats.push('PDF')
    if (attachmentTypes.includes(AttachmentTypes.IMAGE))
      formats.push('JPEG', 'PNG')
    if (attachmentTypes.includes(AttachmentTypes.WORD))
      formats.push('DOC', 'DOCX')
    if (attachmentTypes.includes(AttachmentTypes.EXCEL))
      formats.push('XLS', 'XLSX')
    if (attachmentTypes.includes(AttachmentTypes.CAD))
      formats.push('DWG', 'DXF')
    if (attachmentTypes.includes(AttachmentTypes.VIDEO)) formats.push('MP4')

    return formats.length ? formats : undefined
  }, [attachmentTypes])

  const handleOnAddSiteBoxDocuments = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.stopPropagation()
    const currentSelectedSiteBoxDocuments: SiteBoxDocument[] = value
      .filter(
        (proof) => proof.siteBoxDocumentVersionId || proof.siteBoxDocument,
      )
      .map((proof) => proof.siteBoxDocument)
      .filter(Boolean)

    const handleSelection = (
      selectedSiteboxDocuments: SiteBoxDocument[] | SiteBoxDocument,
    ) => {
      const siteBoxDocuments = Array.isArray(selectedSiteboxDocuments)
        ? selectedSiteboxDocuments
        : [selectedSiteboxDocuments]

      const newProofs: Proof[] = siteBoxDocuments.map(siteBoxDocumentToProof)

      const currentValue = valueRef.current

      // Get non-sitebox documents count
      const nonSiteBoxCount = currentValue.filter(
        (proof) => !proof.siteBoxDocumentVersionId && !proof.siteBoxDocument,
      ).length

      // Only show toast if new selection would exceed maxFiles
      if (!!maxFiles && nonSiteBoxCount + newProofs.length > maxFiles) {
        toast.error(localizedStrings.maximumAttachmentsReached)
        return
      }

      const newValue = produce(currentValue, (draft) => {
        const getProofId = (proof: AttachmentInputValue) =>
          proof.id ?? proof.siteBoxDocument?.id

        const indicesToRemove = []
        draft.forEach((proof: any, index) => {
          if (proof.siteBoxDocumentVersionId || proof.siteBoxDocument) {
            const proofId = getProofId(proof)
            if (!newProofs.some((newProof) => newProof.id === proofId)) {
              indicesToRemove.push(index)
            }
          }
        })

        // Remove indices from the end to avoid disrupting the order
        for (let i = indicesToRemove.length - 1; i >= 0; i--) {
          draft.splice(indicesToRemove[i], 1)
        }

        // Add new proofs if they are not already included
        newProofs.forEach((newProof) => {
          if (!draft.some((proof) => getProofId(proof) === newProof.id)) {
            draft.push(newProof as any)
          }
        })
      })

      updateValue(newValue)
    }

    if (flags.web_site_box_rams_appraisals) {
      showModal(
        <SelectSiteBoxDocumentModal
          organisationId={organisationId}
          projectId={projectId}
          multiSelect={multiple}
          value={currentSelectedSiteBoxDocuments}
          onChange={handleSelection}
          allowedFormats={allowedSiteBoxProofFormats}
        />,
      )
    } else {
      showModal(
        <SelectSiteBoxDocumentModalLegacy
          organisationId={organisationId}
          projectId={projectId}
          multiSelect={multiple}
          currentSelectedSiteBoxDocuments={currentSelectedSiteBoxDocuments}
          handleSiteBoxDocumentSelection={handleSelection}
          allowedFormats={allowedSiteBoxProofFormats}
        />,
      )
    }
  }

  const deleteAttachment = (attachment: { key?: string; uri?: string }) => {
    const newValue = produce(valueRef.current, (draft) => {
      return draft.filter(({ key, uri }) => {
        if (attachment.key) return attachment.key !== key
        return attachment.uri !== uri
      })
    })
    updateValue(newValue)
  }

  const onAnnotate = (
    proof: AttachmentInputValue,
    index: number,
    blob: Blob,
    url: string,
  ) => {
    const next = replaceProofAt(valueRef.current, index, blob, url, proof)
    updateValue(next)
    hideModal()
  }

  const addFiles = useCallback(
    async (newAttachments: Attachments) => {
      const currentValue = valueRef.current

      if (
        !replaceMode &&
        !!maxFiles &&
        currentValue.length + newAttachments.length > maxFiles
      ) {
        toast.error(localizedStrings.maximumAttachmentsReached)
        return
      }

      // Validate all files and filter out invalid ones
      const validatedFiles: Attachments = []

      for (const file of newAttachments) {
        // Validate image files
        if (file.type?.includes('image')) {
          const validation = await validateImageFile(file)

          if (!validation.valid) {
            toast.error(validation.error)
            continue
          }
        }

        // Validate PDF files
        if (file.type?.includes('pdf')) {
          const validation = await validatePdfFile(file)

          if (!validation.valid) {
            toast.error(validation.error)
            continue
          }
        }

        validatedFiles.push(file)
      }

      // If no valid files remain, don't update state
      if (validatedFiles.length === 0) {
        return
      }

      const filesToAdd = replaceMode
        ? validatedFiles.slice(0, maxFiles || 1)
        : validatedFiles

      const newProofsWithUri = filesToAdd.map((file) => {
        file.uri = URL.createObjectURL(file)
        return file
      })

      const baseValue = replaceMode ? [] : currentValue
      const newFiles = [
        ...baseValue,
        ...(newProofsWithUri as unknown as AttachmentInputValue[]),
      ]
      updateValue(newFiles)
    },
    [maxFiles, replaceMode, toast, onChange],
  )

  useEffect(() => {
    if (readOnly) return

    const handlePaste = (event: ClipboardEvent) => {
      const isPastingInsideContainer =
        pasteContainerRef.current?.contains(event.target as Node) === true
      if (!isPastingInsideContainer) {
        return
      }

      const pastedFiles = Array.from(event.clipboardData.files ?? []).filter(
        (file) => {
          const canPasteImage =
            file.type?.startsWith('image/') &&
            attachmentTypes?.includes(AttachmentTypes.IMAGE)
          const canPastePdf =
            file.type === 'application/pdf' &&
            attachmentTypes?.includes(AttachmentTypes.PDF)
          return canPasteImage || canPastePdf
        },
      )

      !!pastedFiles.length && addFiles(pastedFiles)
    }

    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [attachmentTypes, addFiles, readOnly])

  if (readOnly && !value?.length) return null

  return (
    <Box ref={pasteContainerRef} mt={2} {...props}>
      {!!heading && (
        <Flex color='grey.600' alignItems='center'>
          <Text fontSize='14px' mb={1}>
            {heading}
          </Text>

          <Text
            display='flex'
            fontStyle='italic'
            fontSize={13}
            ml={0.5}
            mb={0.5}
          >
            {required
              ? `(${localizedStrings.required})`
              : `(${localizedStrings.optional})`}
            {required && <Box color='error.main'>*</Box>}
          </Text>
        </Flex>
      )}
      {!readOnly && (
        <Dropzone
          accept={acceptTypes}
          onDrop={addFiles}
          multiple={multiple}
          noClick
        >
          {({ getRootProps, getInputProps, open, isFocused }) => {
            return (
              <Flex
                height={120}
                border='1px dashed'
                borderColor={isFocused ? 'primary.main' : borderColor}
                flexDirection='column'
                justifyContent='center'
                alignItems='center'
                borderRadius={1}
                mb={1.5}
                {...getRootProps()}
              >
                <Flex
                  flexDirection='row'
                  justifyContent='center'
                  alignItems='center'
                  width='100%'
                  px={2}
                >
                  <Button
                    variant='contained'
                    sx={{ width: 150, minWidth: 0 }}
                    data-upload-button
                    onClick={open}
                  >
                    {localizedStrings.upload}
                    <input hidden {...getInputProps()} data-test={dataTest} />
                  </Button>

                  {enableLinkSiteBoxDocuments && (
                    <Button
                      variant='outlined'
                      sx={{ width: 250, minWidth: 0, ml: 1 }}
                      onClick={handleOnAddSiteBoxDocuments}
                    >
                      {localizedStrings.chooseFromSitebox}
                    </Button>
                  )}
                </Flex>

                <Text color='grey' mt={1} align='center'>
                  {localizedStrings.attachmentInputDragPrompt}
                </Text>
              </Flex>
            )
          }}
        </Dropzone>
      )}
      <Flex
        flexWrap={isListLayout ? 'nowrap' : 'wrap'}
        flexDirection={isListLayout ? 'column' : 'row'}
        sx={{ gap: 1.5 }}
      >
        {value?.map((proof, index) => {
          const stableKey =
            proof.key ??
            proof.uri ??
            (proof.id != null ? String(proof.id) : String(index))
          const fileName = (proof as File).name
          return (
            <AttachmentFrame
              key={stableKey}
              readOnly={readOnly}
              onDelete={
                proof.nonDeletable ? undefined : () => deleteAttachment(proof)
              }
              showFileName={showFileNames}
              fileName={fileName}
              size={size}
              thumbnailFitToContent={thumbnailFitToContent}
              layout={attachmentLayout}
              dataTest={`upload-thumbnail-${index}`}
              deleteBtnDataTest={`upload-thumbnail-delete-btn-${index}`}
            >
              <AttachmentThumbnail
                proof={proof as Proof}
                warnings={warnings}
                showWarningIcon={
                  !readOnly &&
                  ImageWarning.showProofWarningIcon(warnings, proof.key)
                }
                size={size}
                thumbnailFitToContent={thumbnailFitToContent}
                sx={{ backgroundColor: Palette.Grey[400] }}
                data-test={`upload-thumbnail-${index}`}
                index={index}
                projectId={projectId}
                readOnly={readOnly}
                onAnnotate={(blob, url) =>
                  onAnnotate(proof as AttachmentInputValue, index, blob, url)
                }
                allAttachments={value}
                preventDialogClose={preventDialogClose}
                capturedByName={capturedByName}
              />
            </AttachmentFrame>
          )
        })}
      </Flex>
    </Box>
  )
}

export { AttachmentInput }
